import { ApiError, type Api } from "../api/client.js";
import type { Channel, DraftOutcome } from "../api/types.js";
import type { DraftMatch, FixtureDrafter } from "../adapters/drafter.js";
import { wavOf } from "../adapters/pcm-clip.js";

/**
 * DCTA-111 — which way the customer's words go to be drafted.
 *
 *  - **The model path** (DCTA-97 local, DCTA-108 Tencent ADP): `POST /v1/utterances` with the
 *    words and how they came (`channel`). The bank drafts, validates, decides and builds the card.
 *  - **The fixture path** (the M1 stand-in): a bank that runs no model has no such route and
 *    answers 404 NOT_FOUND; the app then drafts from the reviewed fixtures and posts
 *    `POST /v1/drafts`, as it always has, with the same `channel`.
 *
 * The path is the bank's fact, learnt from its first answer and kept for the session (a new
 * sign-in asks again). It is never a fallback for a failure: once the model path has answered,
 * an error from it — unavailable, busy, unreadable, even a later 404 — is the customer's answer
 * (fail closed). A fixture never stands in for a model that is down.
 */
export type DraftingPath = "unknown" | "model" | "fixture";

/** One request in the chat: the words, how they came, and — once sent — which path drafted them. */
export interface Turn {
  readonly said: string;
  readonly channel: Channel;
  /**
   * The reviewed draft the fixture path posted (the freeze's two readings name its step types);
   * `undefined` on the model path, where only the bank holds the draft. Set by `send`.
   */
  draft: unknown;
  /** Set by `send` before the first byte goes out. */
  path: Exclude<DraftingPath, "unknown"> | undefined;
  /**
   * DCTA-176 (B61, F47): "Ask again" after the identity check was brought up to date — the words the gate refused, sent
   * back exactly as heard; the bank records the turn as `source: reask`. Only the model path takes it.
   */
  readonly source?: "reask";
  /**
   * DCTA-191 (ADR-0024): a spoken request's PCM (adapters/pcm-clip.ts) — sent to the bank as a WAV before the words, which
   * then name it (`audio_id`). Only with `channel: "voice"` and never with `source`.
   */
  readonly audio?: Uint8Array;
}

/** DCTA-191: how long the words wait for the clip's upload before they go without it (the spec: 1.5 s). */
export const AUDIO_WAIT_MS = 1500;

/** The fixture path holds no reviewed draft for these words: the chat says it can't do that one yet. */
export class NoFixtureDraft extends Error {
  constructor() {
    super("no reviewed draft for these words");
    this.name = "NoFixtureDraft";
  }
}

/** SHA-256 of the empty string: the M1 stand-in has no prompt to hash. */
const EMPTY_SHA256 = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";

/**
 * The fixture path's `POST /v1/drafts` body: the reviewed fixture's transcript (its spans are anchored to
 * those bytes) and draft, the stand-in's own id with no prompt, and how the words came.
 */
export function fixtureDraftBody(match: DraftMatch, modelId: string, channel: Channel): { transcript: string; draft: unknown; drafter: { model_id: string; prompt_sha256: string }; channel: Channel } {
  return { transcript: match.transcript, draft: match.draft, drafter: { model_id: modelId, prompt_sha256: EMPTY_SHA256 }, channel };
}

export class Drafting {
  readonly #api: Api;
  readonly #fixtures: FixtureDrafter;
  /** DCTA-188: judge mode's one-shot faulty-drafter switch (app/demo-fault.ts), when the session has one. */
  readonly #demo: { readonly armed: boolean; take(): boolean } | undefined;
  readonly #audioWaitMs: number;
  #path: DraftingPath = "unknown";

  constructor(api: Api, fixtures: FixtureDrafter, demo?: { readonly armed: boolean; take(): boolean }, opts: { readonly audioWaitMs?: number } = {}) {
    this.#api = api;
    this.#fixtures = fixtures;
    this.#demo = demo;
    this.#audioWaitMs = opts.audioWaitMs ?? AUDIO_WAIT_MS;
  }

  /** DCTA-188: the next request is the faulty-drafter demo's — the chat sends it whatever path the bank drafts on. */
  get demoArmed(): boolean {
    return this.#demo?.armed === true;
  }

  get path(): DraftingPath {
    return this.#path;
  }

  /** QA-04: a reload keeps the session, and the path is the session's fact — learnt before the reload, kept across it. */
  restore(path: Exclude<DraftingPath, "unknown">): void {
    if (this.#path === "unknown") this.#path = path;
  }

  /** QA-04: the reviewed draft these words make on the fixture path — an open question's turn, asked again after a reload. */
  draftOf(words: string): unknown {
    return this.#fixtures.draft(words)?.draft;
  }

  /**
   * With a question open, are these words a new request rather than an answer? On the fixture
   * path, words a reviewed fixture holds are a request (the M1 rule). Anywhere else nothing is
   * assumed here: the chat's own reading of the answer decides (`clarify.ts`).
   */
  holds(words: string): boolean {
    return this.#path === "fixture" && this.#fixtures.draft(words) !== undefined;
  }

  /** The words to the bank by the bank's own path; the outcome as the bank answers it. */
  async send(turn: Turn): Promise<DraftOutcome> {
    // DCTA-188 Change 4: judge mode's switch takes ONE request to the demo route — its answer, whatever it is (a 404 from a
    // bank with no model path included), is the customer's answer; nothing falls back to another path.
    if (this.#demo?.take() === true) {
      turn.path = "model";
      return this.#api.faultyDrafter({ transcript: turn.said, channel: turn.channel });
    }
    if (this.#path !== "fixture") {
      turn.path = "model";
      try {
        const outcome = await this.#utter(turn, await this.#upload(turn));
        this.#path = "model";
        return outcome;
      } catch (error) {
        const noRoute = error instanceof ApiError && error.status === 404 && error.reasonCode === "NOT_FOUND";
        if (!noRoute || this.#path === "model") throw error;
        this.#path = "fixture";
      }
    }
    turn.path = "fixture";
    const match = this.#fixtures.draft(turn.said);
    if (match === undefined) throw new NoFixtureDraft();
    turn.draft = match.draft;
    return this.#api.draft(fixtureDraftBody(match, this.#fixtures.modelId, turn.channel));
  }

  /**
   * DCTA-191 (docs/specs/fe-audio-evidence-dcta191.md): a spoken request's clip to the bank before the words — its
   * `audio_id`, or nothing: refused (400, 413, 415, 429, 503 = no audio kept here), unreachable, or slower than the wait.
   * Never the customer's cost: the words never wait longer than `AUDIO_WAIT_MS` for it.
   */
  async #upload(turn: Turn): Promise<string | undefined> {
    if (turn.audio === undefined || turn.channel !== "voice" || turn.source !== undefined) return undefined;
    const kept = this.#api.uploadUtteranceAudio(wavOf(turn.audio)).then(
      (r) => r.audio_id,
      () => undefined,
    );
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<undefined>((resolve) => (timer = setTimeout(() => resolve(undefined), this.#audioWaitMs)));
    try {
      return await Promise.race([kept, late]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * The words, naming their clip when there is one. A clip that already went with another turn (409, a model path's own
   * answer) — or, once the bank has answered on its model path, one it no longer knows (404): nothing happened at the bank,
   * the same words again at once without it, silently. Before the bank's first answer a 404 is the route's own (no model
   * path): it is thrown, the fixture path follows, and the words go to `/v1/utterances` once — the bank cannot say which
   * 404 it is, and a clip lost in the 1.5 s since its upload is not the likely one.
   */
  async #utter(turn: Turn, audioId: string | undefined): Promise<DraftOutcome> {
    const words = { transcript: turn.said, channel: turn.channel, ...(turn.source === undefined ? {} : { source: turn.source }) };
    if (audioId === undefined) return this.#api.utterance(words);
    try {
      return await this.#api.utterance({ ...words, audio_id: audioId });
    } catch (error) {
      const used = error instanceof ApiError && error.reasonCode === "AUDIO_ALREADY_USED";
      const gone = error instanceof ApiError && error.status === 404 && error.reasonCode === "NOT_FOUND" && this.#path === "model";
      if (!used && !gone) throw error;
      return this.#api.utterance(words);
    }
  }
}
